import { BarChart3, Eye, FileText, Headphones, Music, Pause, Play, Volume2, VolumeX, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { getImageUrl } from '../../services/axiosConfig.ts';
import blogAPI from '../../services/blogAPI';
import './BlogPostDetailModal.css';

const BlogPostDetailModal = ({ post, isOpen, onClose, onUpdate }) => {
    const [activeTab, setActiveTab] = useState('preview');
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [podcastEnabled, setPodcastEnabled] = useState(post?.podcast?.isEnabled || false);
    const audioRef = useRef(null);

    useEffect(() => {
        setPodcastEnabled(post?.podcast?.isEnabled || false);
    }, [post]);

    if (!isOpen || !post) return null;

    const handlePlayPause = () => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleTimeUpdate = () => {
        if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
        }
    };

    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            setDuration(audioRef.current.duration);
        }
    };

    const handleSeek = (e) => {
        const seekTime = (e.target.value / 100) * duration;
        if (audioRef.current) {
            audioRef.current.currentTime = seekTime;
            setCurrentTime(seekTime);
        }
    };

    const handleVolumeChange = (e) => {
        const newVolume = e.target.value / 100;
        setVolume(newVolume);
        if (audioRef.current) {
            audioRef.current.volume = newVolume;
        }
        setIsMuted(newVolume === 0);
    };

    const toggleMute = () => {
        if (audioRef.current) {
            const newMutedState = !isMuted;
            setIsMuted(newMutedState);
            audioRef.current.volume = newMutedState ? 0 : volume;
        }
    };

    const formatTime = (time) => {
        if (isNaN(time)) return '0:00';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleTogglePodcast = async () => {
        try {
            const newState = !podcastEnabled;
            await blogAPI.togglePodcast(post._id, newState);
            setPodcastEnabled(newState);
            if (onUpdate) {
                onUpdate();
            }
        } catch (error) {
            console.error('Error toggling podcast:', error);
            alert('Failed to toggle podcast: ' + (error.response?.data?.message || error.message));
        }
    };

    const handleToggleVideo = async () => {
        try {
            const newState = !(post.video?.isEnabled || false);
            await blogAPI.toggleVideo(post._id, newState);
            if (onUpdate) {
                onUpdate();
            }
        } catch (error) {
            console.error('Error toggling video:', error);
            alert('Failed to toggle video: ' + (error.response?.data?.message || error.message));
        }
    };

    return (
        <div className="blog-detail-modal-overlay" onClick={onClose}>
            <div className="blog-detail-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="blog-detail-header">
                    <div className="header-info">
                        <h2>{post.title}</h2>
                        <div className="post-meta">
                            <span className={`status-badge ${post.status}`}>{post.status}</span>
                            {post.publishedAt && (
                                <span className="post-date">
                                    Published {new Date(post.publishedAt).toLocaleDateString()}
                                </span>
                            )}
                        </div>
                    </div>
                    <button className="close-button" onClick={onClose}>
                        <X />
                    </button>
                </div>

                {/* Tabs */}
                <div className="blog-detail-tabs">
                    <button
                        className={`tab ${activeTab === 'preview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('preview')}
                    >
                        <Eye size={18} />
                        Preview
                    </button>
                    <button
                        className={`tab ${activeTab === 'analytics' ? 'active' : ''}`}
                        onClick={() => setActiveTab('analytics')}
                    >
                        <BarChart3 size={18} />
                        Analytics
                    </button>
                    <button
                        className={`tab ${activeTab === 'media' ? 'active' : ''}`}
                        onClick={() => setActiveTab('media')}
                    >
                        <Music size={18} />
                        Media
                    </button>
                </div>

                {/* Content */}
                <div className="blog-detail-content">
                    {/* Preview Tab */}
                    {activeTab === 'preview' && (
                        <div className="preview-tab">
                            {post.featuredImage && (
                                <div className="preview-image">
                                    <img src={post.featuredImage} alt={post.title} />
                                </div>
                            )}
                            <div className="preview-excerpt">
                                <h3>Excerpt</h3>
                                <p>{post.excerpt || 'No excerpt available'}</p>
                            </div>
                            <div className="preview-content">
                                <h3>Content</h3>
                                <div
                                    className="content-preview"
                                    dangerouslySetInnerHTML={{ __html: post.content }}
                                />
                            </div>
                            {post.seo && (
                                <div className="preview-seo">
                                    <h3>SEO Information</h3>
                                    <div className="seo-field">
                                        <strong>SEO Title:</strong> {post.seo.title || 'N/A'}
                                    </div>
                                    <div className="seo-field">
                                        <strong>Meta Description:</strong> {post.seo.description || 'N/A'}
                                    </div>
                                    {post.seo.keywords && post.seo.keywords.length > 0 && (
                                        <div className="seo-field">
                                            <strong>Keywords:</strong> {post.seo.keywords.join(', ')}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Analytics Tab */}
                    {activeTab === 'analytics' && (
                        <div className="analytics-tab">
                            <div className="analytics-grid">
                                <div className="analytics-card">
                                    <div className="analytics-icon">
                                        <Eye />
                                    </div>
                                    <div className="analytics-info">
                                        <div className="analytics-label">Views</div>
                                        <div className="analytics-value">{post.views || 0}</div>
                                    </div>
                                </div>

                                <div className="analytics-card">
                                    <div className="analytics-icon">
                                        <FileText />
                                    </div>
                                    <div className="analytics-info">
                                        <div className="analytics-label">Word Count</div>
                                        <div className="analytics-value">
                                            {post.content ? post.content.replace(/<[^>]*>/g, '').split(/\s+/).length : 0}
                                        </div>
                                    </div>
                                </div>

                                <div className="analytics-card">
                                    <div className="analytics-icon">
                                        <BarChart3 />
                                    </div>
                                    <div className="analytics-info">
                                        <div className="analytics-label">Reading Time</div>
                                        <div className="analytics-value">
                                            {Math.ceil((post.content ? post.content.replace(/<[^>]*>/g, '').split(/\s+/).length : 0) / 200)} min
                                        </div>
                                    </div>
                                </div>

                                {post.podcast && (
                                    <div className="analytics-card">
                                        <div className="analytics-icon">
                                            <Headphones />
                                        </div>
                                        <div className="analytics-info">
                                            <div className="analytics-label">Podcast</div>
                                            <div className="analytics-value">
                                                {post.podcast.isEnabled ? 'Enabled' : 'Disabled'}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="analytics-details">
                                <h3>Post Details</h3>
                                <div className="detail-row">
                                    <span>Author:</span>
                                    <span>{post.author?.name || post.author?.email || 'Unknown'}</span>
                                </div>
                                <div className="detail-row">
                                    <span>Created:</span>
                                    <span>{new Date(post.createdAt).toLocaleString()}</span>
                                </div>
                                <div className="detail-row">
                                    <span>Last Updated:</span>
                                    <span>{new Date(post.updatedAt).toLocaleString()}</span>
                                </div>
                                {post.category && (
                                    <div className="detail-row">
                                        <span>Category:</span>
                                        <span>{post.category.name}</span>
                                    </div>
                                )}
                                {post.tags && post.tags.length > 0 && (
                                    <div className="detail-row">
                                        <span>Tags:</span>
                                        <span>{post.tags.map(tag => tag.name || tag).join(', ')}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Media Tab */}
                    {activeTab === 'media' && (
                        <div className="media-tab">
                            {/* Podcast Section */}
                            <div className="media-section">
                                <div className="media-section-header">
                                    <h3>
                                        <Headphones size={20} />
                                        AI Podcast
                                    </h3>
                                    {post.podcast && (
                                        <label className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={podcastEnabled}
                                                onChange={handleTogglePodcast}
                                            />
                                            <span className="toggle-slider"></span>
                                            <span className="toggle-label">
                                                {podcastEnabled ? 'Enabled' : 'Disabled'}
                                            </span>
                                        </label>
                                    )}
                                </div>

                                {post.podcast && post.podcast.podcastUrl ? (
                                    <div className="podcast-player">
                                        <audio
                                            ref={audioRef}
                                            src={getImageUrl(post.podcast.podcastUrl)}
                                            onTimeUpdate={handleTimeUpdate}
                                            onLoadedMetadata={handleLoadedMetadata}
                                            onEnded={() => setIsPlaying(false)}
                                        />

                                        <div className="player-controls">
                                            <button
                                                className="play-button"
                                                onClick={handlePlayPause}
                                            >
                                                {isPlaying ? <Pause size={24} /> : <Play size={24} />}
                                            </button>

                                            <div className="time-display">
                                                {formatTime(currentTime)}
                                            </div>

                                            <div className="progress-container">
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="100"
                                                    value={(currentTime / duration) * 100 || 0}
                                                    onChange={handleSeek}
                                                    className="progress-bar"
                                                />
                                            </div>

                                            <div className="time-display">
                                                {formatTime(duration)}
                                            </div>

                                            <div className="volume-controls">
                                                <button
                                                    className="volume-button"
                                                    onClick={toggleMute}
                                                >
                                                    {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                                                </button>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="100"
                                                    value={isMuted ? 0 : volume * 100}
                                                    onChange={handleVolumeChange}
                                                    className="volume-slider"
                                                />
                                            </div>
                                        </div>

                                        {post.podcast.metadata && (
                                            <div className="podcast-info">
                                                <div className="info-item">
                                                    <span className="info-label">Voice:</span>
                                                    <span className="info-value">
                                                        {post.podcast.metadata.voice || 'Default'}
                                                    </span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="info-label">Tone:</span>
                                                    <span className="info-value">
                                                        {post.podcast.metadata.tone || 'Professional'}
                                                    </span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="info-label">Generated:</span>
                                                    <span className="info-value">
                                                        {new Date(post.podcast.metadata.generatedAt).toLocaleDateString()}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="no-media-message">
                                        <Headphones size={48} />
                                        <p>No podcast has been generated for this post yet.</p>
                                    </div>
                                )}
                            </div>

                            {/* Video Section */}
                            <div className="media-section">
                                <div className="media-section-header">
                                    <h3>
                                        <FileText size={20} />
                                        AI Video
                                    </h3>
                                    {post.video && (
                                        <label className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={post.video?.isEnabled || false}
                                                onChange={handleToggleVideo}
                                            />
                                            <span className="toggle-slider"></span>
                                            <span className="toggle-label">
                                                {post.video?.isEnabled ? 'Enabled' : 'Disabled'}
                                            </span>
                                        </label>
                                    )}
                                </div>

                                {post.video && post.video.videoUrl ? (
                                    <div className="video-player">
                                        <video controls width="100%">
                                            <source src={post.video.videoUrl} type="video/mp4" />
                                            Your browser does not support the video tag.
                                        </video>
                                        {post.video.metadata && (
                                            <div className="video-info">
                                                <div className="info-item">
                                                    <span className="info-label">Status:</span>
                                                    <span className="info-value">{post.video.status || 'Completed'}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="no-media-message">
                                        <FileText size={48} />
                                        <p>No video has been generated for this post yet.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BlogPostDetailModal;


